import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { reportsApi } from '../api/reports'
import { usersApi } from '../api/users'
import { dashboardApi, type ActionBucket, type DashboardClientRow } from '../api/dashboard'
import { useAuth } from '../contexts/AuthContext'
import { formatDateBR, formatDateTimeBR } from '../lib/format'
import { KnowledgeMeter } from '../components/KnowledgeMeter'

// Pace previsto de fechamento pela semana (dia LOCAL): seg 20% … sex/sáb/dom 100%.
function pacePct(dow: number): number {
  switch (dow) {
    case 1:
      return 0.2
    case 2:
      return 0.4
    case 3:
      return 0.6
    case 4:
      return 0.8
    default:
      return 1.0 // sexta, sábado, domingo
  }
}

const DOW_LABEL = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']

export default function Dashboard() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'

  // Combo só do admin: ver o dashboard com a visão de um gerente específico.
  const [managerId, setManagerId] = useState('')
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    enabled: isAdmin,
  })
  const managers = (usersData?.users ?? [])
    .filter((u) => u.role === 'manager' && u.active !== false)
    .sort((a, b) => a.name.localeCompare(b.name))

  const { data: dash } = useQuery({
    queryKey: ['dashboard', managerId],
    queryFn: () => dashboardApi.get(managerId || undefined),
  })
  const { data: reportsData } = useQuery({
    queryKey: ['reports', {}],
    queryFn: () => reportsApi.list(),
  })

  const reports = reportsData?.reports ?? []
  const unread = reports.filter((r) => !r.isRead)

  const dow = new Date().getDay()
  const pace = pacePct(dow)
  const week = dash?.weekActions
  const previstas = week ? Math.round(week.total * pace) : 0

  const selectedManagerName = managers.find((m) => m.id === managerId)?.name
  const scopeLabel = isAdmin
    ? managerId
      ? `visão de ${selectedManagerName ?? 'gerente'}`
      : 'todos os gerentes'
    : 'suas'

  return (
    <div className="space-y-7">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            Olá, <span className="text-gradient">{user?.name?.split(' ')[0]}</span>
          </h1>
          <p className="mt-1 text-sm text-slate-500">Visão geral das ações e dos clientes.</p>
        </div>
        {isAdmin && (
          <label className="flex items-center gap-2 text-sm">
            <span className="text-slate-500">Ver como:</span>
            <select
              value={managerId}
              onChange={(e) => setManagerId(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            >
              <option value="">Todos (visão admin)</option>
              {managers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {/* ── Portal: cliente que não reconhece alteração na conta (pode ser acesso indevido) ── */}
      {dash && dash.portal.alerts > 0 && (
        <Link
          to="/intelligence"
          className="card flex flex-wrap items-center justify-between gap-3 border-rose-300 bg-rose-50 p-4 transition hover:bg-rose-100/70"
        >
          <div>
            <div className="font-semibold text-rose-900">⚠ Cliente não reconhece alterações na conta</div>
            <div className="text-sm text-rose-800">
              {dash.portal.alerts} aviso{dash.portal.alerts === 1 ? '' : 's'} do "Foi você?" do portal — pode ser acesso indevido.
              Confira os acessos da conta e fale com o cliente.
            </div>
          </div>
          <span className="text-sm font-semibold text-rose-700">Ver →</span>
        </Link>
      )}

      {/* ── Portal: pedidos dos clientes ── */}
      {dash && dash.portal.requests > 0 && (
        <Link
          to="/intelligence"
          className="card flex flex-wrap items-center justify-between gap-3 border-amber-200 bg-amber-50/60 p-4 transition hover:bg-amber-50"
        >
          <div>
            <div className="font-semibold text-slate-900">📨 Pedidos dos clientes</div>
            <div className="text-sm text-slate-600">
              {dash.portal.requests} pedido{dash.portal.requests === 1 ? '' : 's'} pelo portal esperando resposta — a resposta
              aparece para o cliente lá.
            </div>
          </div>
          <span className="text-sm font-semibold text-brand-600">Responder →</span>
        </Link>
      )}

      {/* ── Caixa da IA: o que ela quer confirmar com o gerente ── */}
      {dash && dash.inbox.questions + dash.inbox.proposals > 0 && (
        <Link
          to="/intelligence"
          className="card flex flex-wrap items-center justify-between gap-3 border-violet-200 bg-violet-50/60 p-4 transition hover:bg-violet-50"
        >
          <div>
            <div className="font-semibold text-slate-900">🧠 A IA quer aprender com você</div>
            <div className="text-sm text-slate-600">
              {dash.inbox.questions > 0 &&
                `${dash.inbox.questions} pergunta${dash.inbox.questions === 1 ? '' : 's'} rápida${dash.inbox.questions === 1 ? '' : 's'}`}
              {dash.inbox.questions > 0 && dash.inbox.proposals > 0 && ' e '}
              {dash.inbox.proposals > 0 &&
                `${dash.inbox.proposals} aprendizado${dash.inbox.proposals === 1 ? '' : 's'} para confirmar`}
              {' '}— 1 clique cada, e as próximas análises já usam.
            </div>
          </div>
          <span className="text-sm font-semibold text-brand-600">Responder →</span>
        </Link>
      )}

      {/* ── Área de Ações ── */}
      <section>
        <h2 className="mb-3 text-lg font-bold text-slate-900">
          Ações <span className="text-sm font-normal text-slate-400">({scopeLabel})</span>
        </h2>
        <div className="grid gap-4 lg:grid-cols-2">
          <ActionBar
            title="Ações da semana"
            subtitle="dos relatórios da semana passada e desta"
            bucket={dash?.weekActions}
            previstas={previstas}
            paceLabel={`previsto até ${DOW_LABEL[dow]}: ${previstas} ${
              previstas === 1 ? 'ação' : 'ações'
            }`}
          />
          <ActionBar title="Ações totais" subtitle="todas as ações já criadas" bucket={dash?.totalActions} />
        </div>
      </section>

      {/* ── Área de Clientes ── */}
      <section>
        <h2 className="mb-3 text-lg font-bold text-slate-900">
          Clientes <span className="text-sm font-normal text-slate-400">({scopeLabel})</span>
        </h2>
        <ClientsTable rows={dash?.clients ?? []} />
      </section>

      {/* ── Relatórios não lidos ── */}
      <section>
        <h2 className="mb-3 text-lg font-bold text-slate-900">Relatórios não lidos</h2>
        {unread.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-8 text-center text-sm text-slate-500">
            Tudo em dia — nenhum relatório pendente de leitura. ✨
          </p>
        ) : (
          <div className="card divide-y divide-slate-100 overflow-hidden">
            {unread.map((r) => (
              <Link
                key={r.id}
                to={`/reports/${r.id}`}
                className="flex items-center justify-between px-4 py-3 transition hover:bg-brand-50/50"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900">{r.client.name}</span>
                    {r.hasPendingHighActions && (
                      <span className="chip bg-red-100 text-red-700">Ação urgente</span>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {formatDateBR(r.weekStart)} a {formatDateBR(r.weekEnd)} ·{' '}
                    {formatDateTimeBR(r.generatedAt)}
                  </div>
                </div>
                <span className="text-sm font-medium text-brand-600">Abrir →</span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

// ── Barra de progresso das ações trabalhadas ──
// Na barra da SEMANA a régua tem dois marcadores: o previsto até hoje e o total
// da semana. A escala vai um pouco além do total para o marcador do total não
// colar na borda, e o percentual da legenda é sobre o PREVISTO (é a meta do
// dia) — passa de 100% quando o time trabalha mais do que o previsto.
function ActionBar({
  title,
  subtitle,
  bucket,
  previstas,
  paceLabel,
}: {
  title: string
  subtitle: string
  bucket?: ActionBucket
  previstas?: number
  paceLabel?: string
}) {
  const b = bucket ?? { total: 0, trabalhadas: 0, feito: 0, comentarios: 0 }
  const isWeek = previstas !== undefined
  const alvo = previstas ?? 0

  // Escala da barra (posição visual). Só a barra da semana ganha folga.
  const escala = Math.max(b.total, 1) * (isWeek ? 1.15 : 1)
  const pos = (n: number) => Math.min(100, (n / escala) * 100)
  // Rótulo é centrado no marcador; afasta das bordas para não cortar o texto
  // (ex.: previsto=0 na segunda, com cliente de poucas ações).
  const posRotulo = (n: number) => Math.min(94, Math.max(6, pos(n)))

  // Base dos percentuais: previsto na semana, total no card de totais.
  const base = isWeek ? alvo : b.total
  const pct = (n: number) => (base > 0 ? (n / base) * 100 : 0)

  // Na sexta em diante o pace é 100%, então previsto == total e os dois
  // marcadores caem no mesmo ponto — vira um rótulo só.
  const marcadoresJuntos = isWeek && alvo === b.total
  const showRegua = isWeek && b.total > 0

  return (
    <div className="card p-5">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <p className="text-xs text-slate-400">{subtitle}</p>
        </div>
        <span className="shrink-0 text-sm text-slate-500">
          <strong className="text-slate-900">{b.total}</strong> ações
        </span>
      </div>

      {showRegua && (
        <div className="relative mb-1 h-4 text-[10px] font-medium text-slate-500">
          {marcadoresJuntos ? (
            <span
              className="absolute -translate-x-1/2 whitespace-nowrap"
              style={{ left: `${posRotulo(b.total)}%` }}
            >
              previsto = total
            </span>
          ) : (
            <>
              <span
                className="absolute -translate-x-1/2 whitespace-nowrap"
                style={{ left: `${posRotulo(alvo)}%` }}
              >
                previsto
              </span>
              <span
                className="absolute -translate-x-1/2 whitespace-nowrap"
                style={{ left: `${posRotulo(b.total)}%` }}
              >
                total
              </span>
            </>
          )}
        </div>
      )}

      <div className="relative h-7 overflow-hidden rounded-lg bg-slate-100">
        {/* trabalhadas (não-pendentes) — única faixa preenchida */}
        <div
          className="absolute inset-y-0 left-0 bg-brand-400 transition-all"
          style={{ width: `${pos(b.trabalhadas)}%` }}
        />
        {showRegua && (
          <>
            {/* total da semana */}
            <div
              className="absolute inset-y-0 z-10 w-[3px] bg-slate-400"
              style={{ left: `calc(${pos(b.total)}% - 1.5px)` }}
              title={`Total da semana: ${b.total}`}
            />
            {/* alvo previsto até hoje */}
            <div
              className="absolute inset-y-0 z-10 w-[3px] bg-slate-800"
              style={{ left: `calc(${pos(alvo)}% - 1.5px)` }}
              title={`Previsto até hoje: ${alvo}`}
            />
          </>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <Legend swatch="bg-brand-400" label="Trabalhadas" n={b.trabalhadas} pct={pct(b.trabalhadas)} />
        {isWeek && (
          <span className="inline-flex items-center gap-1.5 text-slate-600">
            <span className="inline-block h-3 w-[3px] bg-slate-800" />
            Previsto: <strong className="text-slate-900">{alvo}</strong>
          </span>
        )}
        {paceLabel && <span className="ml-auto text-slate-400">{paceLabel}</span>}
      </div>
    </div>
  )
}

function Legend({
  swatch,
  label,
  n,
  pct,
}: {
  swatch: string
  label: string
  n: number
  pct: number
}) {
  return (
    <span className="inline-flex items-center gap-1.5 text-slate-600">
      <span className={`inline-block h-2.5 w-2.5 rounded-sm ${swatch}`} />
      {label}: <strong className="text-slate-900">{n}</strong>
      <span className="text-slate-400">({Math.round(pct)}%)</span>
    </span>
  )
}

// ── Tabela de clientes ──
function ClientsTable({ rows }: { rows: DashboardClientRow[] }) {
  if (rows.length === 0) {
    return (
      <p className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-8 text-center text-sm text-slate-500">
        Nenhum cliente no seu escopo.
      </p>
    )
  }
  return (
    <div className="card overflow-x-auto">
      <table className="w-full min-w-[860px] text-sm">
        <thead>
          <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-500">
            <th className="px-4 py-3">Cliente</th>
            <th className="px-4 py-3" title="Quanto a IA sabe do cliente: dossiê, metas, contexto e retorno recente">
              IA sabe
            </th>
            <th className="px-4 py-3 text-right">Relatórios</th>
            <th className="px-4 py-3 text-right">Ações criadas</th>
            <th className="px-4 py-3 text-right">Análises</th>
            <th className="px-4 py-3 text-right">Trabalhadas</th>
            <th className="px-4 py-3 text-right">Feito</th>
            <th className="px-4 py-3 text-right">Comentários</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((r) => {
            const pct = (n: number) =>
              r.actionsCreated > 0 ? Math.round((n / r.actionsCreated) * 100) : 0
            return (
              <tr key={r.id} className="hover:bg-slate-50/50">
                <td className="px-4 py-3 font-medium text-slate-900">{r.name}</td>
                <td className="px-4 py-3">
                  {r.knowledge ? (
                    <Link to="/intelligence" className="block">
                      <KnowledgeMeter
                        compact
                        score={r.knowledge.score}
                        level={r.knowledge.level}
                        label={r.knowledge.label}
                        missingSections={r.knowledge.missingSections}
                      />
                    </Link>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right text-slate-700">{r.reports}</td>
                <td className="px-4 py-3 text-right text-slate-700">{r.actionsCreated}</td>
                <td className="px-4 py-3 text-right text-slate-700">{r.analyses}</td>
                <td className="px-4 py-3 text-right">
                  <NumPct n={r.trabalhadas} pct={pct(r.trabalhadas)} />
                </td>
                <td className="px-4 py-3 text-right">
                  <NumPct n={r.feito} pct={pct(r.feito)} />
                </td>
                <td className="px-4 py-3 text-right">
                  <NumPct n={r.comentarios} pct={pct(r.comentarios)} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function NumPct({ n, pct }: { n: number; pct: number }) {
  return (
    <span className="whitespace-nowrap text-slate-900">
      <strong>{n}</strong> <span className="text-xs text-slate-400">{pct}%</span>
    </span>
  )
}
